import React from "react";
import { KPI_LIST } from "../constants/kpi";

/* KPI 型 */
export interface KpiSnapshot {
    year_month: string;
    period_start: string;
    period_end: string;

    sales: number;

    total_promotion: number;
    total_promotion_rate: number;

    total_advertising: number;
    total_advertising_rate: number;

    marginal_profit: number;
    marginal_profit_rate: number;

    operating_profit_after_fixed?: number;
    operating_profit_after_fixed_rate?: number;

    point_amount_spent: number;
    point_amount_spent_rate: number;

    coupon_shop: number;
    coupon_rate: number;

    mail_amount_spent: number;
    mail_amount_spent_rate: number;

    afl_amount_spent: number;
    afl_amount_spent_rate: number;
}

interface Props {
    current?: KpiSnapshot | null; // ★ null/undefined許容（壊れない）
    compare?: KpiSnapshot | null; // ★ null/undefined許容（壊れない）
    visible?: Record<string, boolean> | null; // ★ null/undefined許容（壊れない）
    loading?: boolean; // ★任意：ローディング表示
}

/* util */
function diffPct(current?: number | null, prev?: number | null) {
    if (current == null || prev == null) return "—";
    if (!prev) return "0%";
    const raw = (current / prev) * 100 - 100;
    const sign = raw < 0 ? "▲" : "+";
    return sign + Math.abs(raw).toFixed(1) + "%";
}

function buildPeriodLabel(yearMonth?: string | null, periodEnd?: string | null): string {
    if (!yearMonth || !periodEnd) return "—";
    const ym = String(yearMonth);
    const pe = String(periodEnd);
    const [y, m] = ym.slice(0, 7).split("-");
    if (!y || !m) return "—";
    const end = pe.replace(/-/g, "/");
    return `${y}/${m}/01〜${end}`;
}

/* Card */
const KpiCard: React.FC<any> = ({
    title,
    value,
    rateLabel,
    period,
    compareLabel,
    diffLabel,
    compareValue,
    comparePeriod,
    loading,
}) => {
    const isDash = diffLabel === "—";
    const negative = !isDash && String(diffLabel).includes("▲");

    const diffClass = isDash
        ? "bg-slate-50 text-slate-600 border-slate-200"
        : negative
            ? "bg-rose-50 text-rose-600 border-rose-200"
            : "bg-emerald-50 text-emerald-600 border-emerald-200";

    return (
        <div className="rounded-2xl border border-slate-300 bg-white px-4 py-3 text-center shadow-sm">
            <div className="text-lg font-bold text-slate-800">
                {title}
                {rateLabel && (
                    <span className="ml-1 text-sm text-slate-500">（{rateLabel}）</span>
                )}
            </div>

            <div className="mt-1 text-3xl font-extrabold">
                {loading ? (
                    <div className="mx-auto h-9 w-40 animate-pulse rounded bg-slate-100" />
                ) : value !== "—" ? (
                    <>
                        {value}
                        <span className="ml-0.5 text-xl">円</span>
                    </>
                ) : (
                    value
                )}
            </div>

            <div className="text-xs text-slate-500">
                {loading ? (
                    <div className="mx-auto mt-1 h-3 w-32 animate-pulse rounded bg-slate-100" />
                ) : (
                    period
                )}
            </div>

            <div className="my-1 h-px bg-slate-300" />

            <div className="text-sm font-medium text-slate-700">
                {loading ? (
                    <div className="mx-auto h-5 w-28 animate-pulse rounded bg-slate-100" />
                ) : compareValue !== "—" ? (
                    <>
                        {compareValue}
                        <span className="ml-0.5 text-xs">円</span>
                    </>
                ) : (
                    compareValue
                )}
            </div>

            <div className="text-xs text-slate-500">
                {loading ? (
                    <div className="mx-auto mt-1 h-3 w-28 animate-pulse rounded bg-slate-100" />
                ) : (
                    comparePeriod
                )}
            </div>

            <div className="mt-1 flex justify-center gap-2 text-xs">
                <span className="text-slate-500">{compareLabel}</span>
                <span className={`px-2 py-0.5 rounded-full font-bold border ${diffClass}`}>
                    {loading ? "…" : diffLabel}
                </span>
            </div>
        </div>
    );
};

export default function KpiCards({ current, compare, visible, loading = false }: Props) {
    const formatNumber = (v?: number | null, round?: boolean) => {
        if (v == null || Number.isNaN(Number(v))) return "—";
        const n = round ? Math.round(Number(v)) : Number(v);
        return n.toLocaleString("ja-JP");
    };

    const pct = (v?: number | null) =>
        v == null || Number.isNaN(Number(v)) ? "—" : (Number(v) * 100).toFixed(1) + "%";

    const curPeriod = buildPeriodLabel(current?.year_month, current?.period_end);
    const cmpPeriod = buildPeriodLabel(compare?.year_month, compare?.period_end);

    const compareLabel =
        compare?.year_month
            ? compare.year_month.replace("-01", "").replace(/-/g, "/") + " 比"
            : "—";

    // ★visible が無い/未準備でも壊れない（無ければ全部表示扱い）
    const isVisible = (key: string) => (visible ? visible[key] !== false : true);

    return (
        <div className="grid gap-6 grid-cols-[repeat(auto-fit,minmax(260px,1fr))]">
            {KPI_LIST.map((k) => {
                if (!isVisible(k.key)) return null;

                const curVal = (current as any)?.[k.key];
                const cmpVal = (compare as any)?.[k.key];
                const curRate = k.rateKey ? (current as any)?.[k.rateKey] : undefined;

                return (
                    <KpiCard
                        key={k.key}
                        title={k.title}
                        value={formatNumber(curVal, k.round)}
                        rateLabel={k.rateKey ? pct(curRate) : undefined}
                        period={curPeriod}
                        compareLabel={compareLabel}
                        diffLabel={diffPct(
                            curVal == null ? null : Number(curVal),
                            cmpVal == null ? null : Number(cmpVal)
                        )}
                        compareValue={formatNumber(cmpVal, k.round)}
                        comparePeriod={cmpPeriod}
                        loading={loading}
                    />
                );
            })}
        </div>
    );
}
